// src/components/nexilink/action-button.tsx
// Domain-specific buttons built on shadcn/ui Button - CLEAN SOLID COLORS
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Primary action button — brand dark green
interface ActionButtonProps extends Omit<ButtonProps, 'variant'> {
    icon?: LucideIcon;
    // iconNode renders a custom icon element instead of a lucide `icon`
    // (e.g. a brand mark like <LinkAvatar/>). Takes precedence over
    // `icon` when both are set. Caller controls its sizing.
    iconNode?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    loading?: boolean;
}

export function ActionButton({
                                 children,
                                 icon: Icon,
                                 iconNode,
                                 iconPosition = 'left',
                                 loading = false,
                                 className,
                                 disabled,
                                 ...props
                             }: ActionButtonProps) {
    const renderedIcon = iconNode ?? (Icon ? <Icon className="w-4 h-4" /> : null);
    return (
        <Button
            className={cn(
                'bg-[#1A4D4F] text-[#f5f5f5] font-semibold',
                'hover:bg-[#1A4D4F]/90',
                'transition-all duration-200',
                className
            )}
            disabled={disabled || loading}
            {...props}
        >
            {loading && (
                <div className="w-4 h-4 border-2 border-[#f5f5f5] border-t-transparent rounded-full animate-spin" />
            )}
            {!loading && renderedIcon && iconPosition === 'left' && renderedIcon}
            {children}
            {!loading && renderedIcon && iconPosition === 'right' && renderedIcon}
        </Button>
    );
}

// Secondary outline button
export function OutlineButton({
                                  children,
                                  icon: Icon,
                                  iconNode,
                                  className,
                                  ...props
                              }: ActionButtonProps) {
    return (
        <Button
            variant="outline"
            className={cn(
                'border-2 border-[#1A4D4F] text-[#1A4D4F] font-medium hover:bg-[#A4C3B2]/30',
                className
            )}
            {...props}
        >
            {iconNode ?? (Icon && <Icon className="w-4 h-4" />)}
            {children}
        </Button>
    );
}

// Danger button
export function DangerButton({
                                 children,
                                 icon: Icon,
                                 className,
                                 ...props
                             }: ActionButtonProps) {
    return (
        <Button
            variant="destructive"
            className={cn('font-medium', className)}
            {...props}
        >
            {Icon && <Icon className="w-4 h-4" />}
            {children}
        </Button>
    );
}